import dayjs from 'dayjs';
import { formatDateByLocale } from 'utils/formattingUtil';

import {
  EReportStatus,
  EReportStatusSearchOrder,
  reportStatusDisplayLabels
} from '../types/report.types';
import { ESortDirection } from '../types/searchReports.types';

const CREATED_DATE_LABEL_PREFIX = 'Report created';

export const reportsStatusFilterOptions: {
  value: EReportStatus;
  label: string;
}[] = EReportStatusSearchOrder.map((status) => ({
  value: status,
  label: reportStatusDisplayLabels[status]
}));

export const reportsSortFilterOptions: {
  value: ESortDirection;
  label: string;
}[] = [
  { value: ESortDirection.DESC, label: 'Newest first' },
  { value: ESortDirection.ASC, label: 'Oldest first' }
];

const extractCalendarDay = (dateString: string) =>
  dateString.includes('T')
    ? dateString.split('T')[0] ?? dateString
    : dateString;

export const serializeCreatedDateFilterValue = (
  createdAfter: string | undefined,
  createdBefore: string | undefined
): string => {
  if (createdAfter && createdBefore) {
    return JSON.stringify({
      startDate: createdAfter,
      endDate: createdBefore
    });
  }
  return '';
};

export const getCreatedDateFilterDisplay = (
  createdAfter: string | undefined,
  createdBefore: string | undefined
): {
  displayLabel: string;
  displayParts: [string, string] | undefined;
} => {
  if (!createdAfter || !createdBefore) {
    return { displayLabel: CREATED_DATE_LABEL_PREFIX, displayParts: undefined };
  }
  const start = dayjs(extractCalendarDay(createdAfter));
  const end = dayjs(extractCalendarDay(createdBefore));
  if (start.isSame(end, 'day')) {
    const formattedSingle = formatDateByLocale(start.toISOString());
    return {
      displayLabel: `${CREATED_DATE_LABEL_PREFIX} | ${formattedSingle}`,
      displayParts: [CREATED_DATE_LABEL_PREFIX, formattedSingle]
    };
  }
  const dateRange = `${formatDateByLocale(
    start.toISOString()
  )} - ${formatDateByLocale(end.toISOString())}`;
  return {
    displayLabel: `${CREATED_DATE_LABEL_PREFIX} | ${dateRange}`,
    displayParts: [CREATED_DATE_LABEL_PREFIX, dateRange]
  };
};

export const getSortFilterBannerLabel = (
  sortDirection: ESortDirection
): string => {
  const current = reportsSortFilterOptions.find(
    (option) => option.value === sortDirection
  );
  return `Sort: ${current?.label ?? 'Newest first'}`;
};

export const tryParseDateRangeFromFilter = (
  value: string
): { startDate: string; endDate: string } | null => {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as {
      startDate?: string;
      endDate?: string;
    };
    if (parsed.startDate && parsed.endDate) {
      return { startDate: parsed.startDate, endDate: parsed.endDate };
    }
  } catch {
    return null;
  }
  return null;
};
